import { billingService } from '@/services/billing.service';

const initialState = () => ({
  tenants: [],
  unlinkedCustomers: [],
  // The caller's own billing state, for the banners and the tenant billing page. Comes from
  // GET /billing/me, which takes no tenant parameter — the backend reads it from the JWT.
  mine: null,
  status: {
    error: null,
    pending: false,
    resolved: false,
    rejected: false,
  },
});

const state = initialState();

const actions = {
  getBillingTenants({ commit }, { needsAttentionOnly = false } = {}) {
    commit('PENDING');
    return billingService.getTenants({ needsAttentionOnly }).then(
      (tenants) => {
        commit('TENANTS_SUCCESS', tenants);
      },
      (error) => {
        commit('ERROR', error.response);
      },
    );
  },

  /**
   * The caller's own billing state.
   *
   * Resolves to null rather than rejecting when there is no Stripe customer, because that is the
   * normal state for a trial tenant, an Entreprise account, or anyone at all before phase 0 — and
   * treating it as an error would put a failure banner in front of all of them.
   */
  getMyBilling({ commit }) {
    return billingService.getMine().then(
      (mine) => {
        commit('MINE_SUCCESS', mine);
      },
      (error) => {
        // 404 is "no Stripe customer". Anything else keeps the last state, so a blip cannot hide a banner.
        if (error && error.response && error.response.status === 404) commit('MINE_SUCCESS', null);
      },
    );
  },

  getUnlinkedCustomers({ commit }) {
    return billingService.getUnlinkedCustomers().then(
      (customers) => {
        commit('UNLINKED_SUCCESS', customers);
      },
      (error) => {
        commit('ERROR', error.response);
      },
    );
  },

  /**
   * Resolves with the sync result so the page can say what happened, and rethrows so it can say when
   * nothing did. Refreshes both lists either way — a partial run still changes what the table shows
   * and what the link dialog can offer.
   */
  sync({ commit, dispatch }) {
    commit('PENDING');
    return billingService.sync().then(
      (result) => {
        commit('CRUD_SUCCESS');
        return Promise.all([dispatch('getBillingTenants'), dispatch('getUnlinkedCustomers')]).then(() => result);
      },
      (error) => {
        commit('ERROR', error.response);
        throw error;
      },
    );
  },

  /**
   * Rethrows on failure so the dialog can tell a 409 (already linked — the user picked the wrong
   * row, and should be told precisely that) and a 404 (Stripe has no such customer) apart from
   * anything else. Swallowing it here would leave the operator staring at an unchanged table with
   * no explanation.
   */
  linkCustomer({ commit, dispatch }, { tenantId, stripeCustomerId }) {
    commit('PENDING');
    return billingService.linkCustomer({ tenantId, stripeCustomerId }).then(
      () => {
        commit('CRUD_SUCCESS');
        // Both lists change: the tenant gains a row, the customer leaves the unlinked work list.
        return Promise.all([dispatch('getBillingTenants'), dispatch('getUnlinkedCustomers')]);
      },
      (error) => {
        commit('ERROR', error.response);
        throw error;
      },
    );
  },

  unlinkCustomer({ commit, dispatch }, tenantId) {
    commit('PENDING');
    return billingService.unlinkCustomer(tenantId).then(
      () => {
        commit('CRUD_SUCCESS');
        return Promise.all([dispatch('getBillingTenants'), dispatch('getUnlinkedCustomers')]);
      },
      (error) => {
        commit('ERROR', error.response);
        throw error;
      },
    );
  },

  resync({ commit, dispatch }, tenantId) {
    commit('PENDING');
    return billingService.resync(tenantId).then(
      () => {
        commit('CRUD_SUCCESS');
        return dispatch('getBillingTenants');
      },
      (error) => {
        commit('ERROR', error.response);
        throw error;
      },
    );
  },
};

const mutations = {
  PENDING(state) {
    state.status = { error: null, pending: true, resolved: false, rejected: false };
  },
  TENANTS_SUCCESS(state, tenants) {
    state.tenants = tenants;
    state.status = { error: null, pending: false, resolved: true, rejected: false };
  },
  MINE_SUCCESS(state, mine) {
    state.mine = mine;
  },
  UNLINKED_SUCCESS(state, customers) {
    state.unlinkedCustomers = customers;
  },
  CRUD_SUCCESS(state) {
    state.status = { error: null, pending: false, resolved: true, rejected: false };
  },
  ERROR(state, error) {
    state.status = { error, pending: false, resolved: false, rejected: true };
  },
  RESET(state) {
    Object.assign(state, initialState());
  },
};

const getters = {
  tenants: (state) => state.tenants,
  unlinkedCustomers: (state) => state.unlinkedCustomers,
  mine: (state) => state.mine,
  pending: (state) => state.status.pending,
  error: (state) => state.status.error,

  /** True while the tenant's latest invoice is unpaid or needs action. */
  hasPaymentIssue: (state) => !!(state.mine && state.mine.paymentIssue),

  /** The hosted Stripe invoice, so the banner's action can go straight to paying it. */
  latestInvoiceUrl: (state) => (state.mine ? state.mine.latestInvoiceUrl : null),

  /**
   * Card tone, but only when it warrants saying something.
   *
   * `ok` returns null so the banner is absent rather than reassuring — nobody needs telling their
   * card is fine, and a permanent banner is a banner people stop reading.
   */
  cardWarningTone: (state) => {
    if (!state.mine || !state.mine.cardExpiryTone) return null;
    // Nothing bills the card any more: the subscription-ended message says what matters instead.
    if (state.mine.subscriptionBilling === false) return null;
    return state.mine.cardExpiryTone === 'ok' ? null : state.mine.cardExpiryTone;
  },

  /** Active account, linked to Stripe, that nothing bills: cancelled, paused or never subscribed. */
  subscriptionEnded: (state) => !!(state.mine && state.mine.activeWithoutSubscription === true),

  cardExpiry: (state) => (state.mine ? state.mine.cardExpiry : null),

  /** The plan to show on the tenant billing page. Null when there is no Stripe subscription. */
  myPlan: (state) => {
    if (!state.mine || !state.mine.subscriptionStatus) return null;
    return {
      planCode: state.mine.planCode,
      planInterval: state.mine.planInterval,
      priceNickname: state.mine.priceNickname,
      status: state.mine.subscriptionStatus,
      currentPeriodEnd: state.mine.currentPeriodEnd,
      hasBiItem: state.mine.biItemBilled,
      monthlyAmount: state.mine.monthlyAmount,
      ended: state.mine.subscriptionBilling === false,
    };
  },
};

export const billing = {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
